import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BcMessagesManager } from '../widgets';
import { MITEK_MESSAGES_MANAGER } from '../translations/translation.injectors';
import { Logger, StepsStoreService } from '../core/services';
import { DocumentTypes, DocumentTypesShortcat, IdType, ImageTypes, PassportSide } from '../models/documentType';
import { MitekMode } from '../models/mitekMode';
import { MitekScienceCommands } from '../models/mitekScienceCommands';
import { MitekErrorStatuses, MitekFeedbackMessages, MitekResultStatus } from '../models/mitekStatuses';

import { MitekConfig } from '../shared/mitek-config';
import { IdentificationSteps } from '@app/models/identificationSteps';
import { CameraService } from '@app/core/services/camera.service';

declare global {
  interface Window {
    mitekScienceSDK: any;
  }
}

@Component({
  selector: 'app-document-capture',
  templateUrl: './document-capture.component.html',
  styleUrls: ['./document-capture.component.scss']
})
export class DocumentCaptureComponent implements OnInit {
  cameraDisplayStarted: boolean;
  cameraProcessingStarted: boolean;
  captureStarted: boolean;
  cameraPermissionStatus: PermissionState | null = null;

  errorStatus: MitekErrorStatuses;
  errorMessages: string;

  processingNotStartedTimeout: any;
  autoCaptureTimeout: any;
  private readonly processingNotStartedTimeoutLength = 15000;
  private readonly autoCaptureTimeoutLength = 30000;
  private readonly hintsTimeout = 3000;

  constructor(
    @Inject(MITEK_MESSAGES_MANAGER) public mitekMessagesManager: BcMessagesManager,
    public stepsStore: StepsStoreService,
    private logger: Logger,
    private router: Router,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private cameraService: CameraService
  ) {}

  back() {
    this.router.navigate(['/select-document'], {queryParamsHandling: 'merge'});
  }

  isPassportUpload(step) {
    return step.documentType === DocumentTypes.Passport || step.documentType === DocumentTypes.QR_BARCODE;
  }

  isPassportFrontside(step) {
    return step.documentType === DocumentTypes.Passport && step.passportSide === PassportSide.FRONT;
  }

  isPassportBackside(step) {
    return step.documentType === DocumentTypes.QR_BARCODE && step.passportSide === PassportSide.BACK;
  }

  isIdFrontSideUpload(step) {
    return step.documentType === DocumentTypes.DLFRONT && step.idSide === IdType.FRONT;
  }

  isIdBackSideUpload(step) {
    return step.documentType === DocumentTypes.DLFRONT && step.idSide === IdType.BACK;
  }

  ngOnInit() {
    this.checkCameraStatus();
  }

  clearTimers() {
    clearTimeout(this.autoCaptureTimeout);
    clearTimeout(this.processingNotStartedTimeout);
    this.autoCaptureTimeout = null;
    this.processingNotStartedTimeout = null;
  }

  captureDocument() {
    this.captureStarted = true;
    this.cameraDisplayStarted = false;
    this.cameraProcessingStarted = false;
    this.clearTimers();

    this.ngZone.runOutsideAngular(() => {
      this.initCapture(this.errorStatus ? MitekMode.MANUAL_CAPTURE : MitekMode.AUTO_CAPTURE);
    });
  }

  initCapture(mode: string) {
    this.errorStatus = null;
    this.errorMessages = null;

    if (this.isAutoCapture(mode)) {
      if (!this.processingNotStartedTimeout) {
        this.processingNotStartedTimeout = setTimeout(() => {
          if (!this.cameraProcessingStarted || !this.cameraDisplayStarted) {
            window.mitekScienceSDK.cmd(MitekScienceCommands.SDK_STOP);

            this.ngZone.run(() => {
              this.captureStarted = false;
              this.errorStatus = MitekErrorStatuses.ERROR;
              this.errorMessages = this.mitekMessagesManager.translations['ERROR_CAMERA_STUCK'];
              this.logger.error(`cameraProcess: ${this.cameraProcessingStarted}, cameraDisplay: ${this.cameraProcessingStarted}`, 'Camera error');
            });
          }
        }, this.processingNotStartedTimeoutLength);
      }
    }

    window.mitekScienceSDK.cmd(MitekScienceCommands.CAPTURE_AND_PROCESS_FRAME, {
      ...MitekConfig,
      ...{
        mode: mode,
        documentType: this.stepsStore.step.documentType
      }
    });

    window.mitekScienceSDK.on(MitekScienceCommands.SDK_ERROR, err => {
      this.ngZone.run(() => {
        this.captureStarted = false;
        this.clearTimers();

        if (err) {
          this.errorStatus = err?.type;

          this.checkCameraStatus();

          this.errorMessages = `${this.mitekMessagesManager.translations[err?.type]}`;
          this.logger.error(`${err?.code} ${err?.type}`, MitekScienceCommands.SDK_ERROR);
        } else {
          this.errorStatus = MitekErrorStatuses.ERROR;
          this.errorMessages = this.mitekMessagesManager.translations[MitekErrorStatuses.ERROR];
          this.logger.error(this.errorStatus, MitekScienceCommands.SDK_ERROR);
        }
      });
    });

    if (this.isAutoCapture(mode)) {
      window.mitekScienceSDK.on(MitekScienceCommands.CAMERA_DISPLAY_STARTED, () => {
        this.ngZone.run(() => {
          this.cameraDisplayStarted = true;

          this.toastr.info(
            this.mitekMessagesManager.translations['LOADING'],
            this.mitekMessagesManager.translations['LOADING_TITLE'],
            {closeButton: true, positionClass: 'toast-top-center'}
          );
        });

        window.mitekScienceSDK.cmd(MitekScienceCommands.SHOW_HINT,
          {
            options: {
              hintText: this.mitekMessagesManager.translations['MITEK_INITIAL_HINT'],
              hintDuration: this.hintsTimeout
            }
          }
        );
      });
    }

    window.mitekScienceSDK.on(MitekScienceCommands.FRAME_PROCESSING_FEEDBACK, (result) => {
      try {
        const recentHint = result.key;

        if (recentHint) {
          const hintMessages = recentHint === MitekFeedbackMessages.MITEK_ERROR_FOUR_CORNER ?
            this.mitekMessagesManager.translations['MITEK_FOUR_CORNER_MESSAGE'] :
            this.mitekMessagesManager.translations[recentHint];
          window.mitekScienceSDK.cmd(MitekScienceCommands.SHOW_HINT,
            {
              options: {
                hintText: hintMessages,
                hintDuration: this.hintsTimeout
              }
            });

          if (!this.isAutoCapture(mode)) {
            this.ngZone.run(() => {
              this.captureStarted = false;
              this.toastr.info(hintMessages || recentHint, '', {closeButton: true, positionClass: 'toast-top-center'});
            });
          }
        }
      } catch (error) {
        this.logger.error(this.errorStatus, `AutocaptureProcessingFeedback - ${error}`);
      }
    });

    window.mitekScienceSDK.on(MitekScienceCommands.FRAME_PROCESSING_STARTED, () => {
      this.ngZone.run(() => {
        this.cameraProcessingStarted = true;

        this.toastr.clear();
      });

      if (!this.autoCaptureTimeout) {
        this.autoCaptureTimeout = setTimeout(() => this.stopAutoCapture(), this.autoCaptureTimeoutLength);
      }
    });

    window.mitekScienceSDK.on(MitekScienceCommands.FRAME_CAPTURE_RESULT, this.frameCaptureResult.bind(this));
  }

  private isAutoCapture(mode: string) {
    return mode === MitekMode.AUTO_CAPTURE;
  }

  private stopAutoCapture() {
    window.mitekScienceSDK.cmd(MitekScienceCommands.SDK_STOP);

    this.ngZone.run(() => {
      this.captureStarted = false;
      this.errorStatus = MitekErrorStatuses.CAMERA_TIMEOUT;
      this.errorMessages = this.mitekMessagesManager.translations['CAMERA_TIMEOUT'];
    });
  }

  private frameCaptureResult(result) {
    this.ngZone.run(() => {
      this.captureStarted = false;
      clearTimeout(this.autoCaptureTimeout);
      clearTimeout(this.processingNotStartedTimeout);

      const resultStatus = result.response.status;
      if (resultStatus === MitekResultStatus.Failure) {
        const err = result.response.warnings;
        this.errorStatus = err?.key;
        this.errorMessages = `${this.mitekMessagesManager.translations[err?.key]}`;
        this.logger.error(`${err?.code} ${err?.key}`, MitekScienceCommands.SDK_ERROR);

        return;
      }

      this.stepsStore.step.previewImage = result.response.image;

      const documentType = result.response.docType;

      switch (documentType) {
        case DocumentTypes.Passport:
          this.stepsStore.step.previewImage = {
            documentType: DocumentTypesShortcat.Passport,
            imageType: ImageTypes.Passport,
            imageData: result.response.imageData
          };
          this.stepsStore.step.mrz = result.response.mrz;
          break;

        case DocumentTypes.DLFRONT:
          this.stepsStore.step.previewImage = {
            documentType: DocumentTypesShortcat.ID,
            imageType: this.stepsStore.step.idSide,
            imageData: result.response.imageData
          };
          break;

        case DocumentTypes.QR_BARCODE:
          this.stepsStore.step.previewImage = {
            documentType: DocumentTypesShortcat.Passport,
            imageType: ImageTypes.PassportBack,
            imageData: result.response.imageData
          };
          this.stepsStore.step.qrCode = result.response.code;
          break;

        default:
          this.logger.error(`${documentType} type isn't implemented`);
          console.error(`${documentType} type isn't implemented`);
      }

      this.stepsStore.step.success = true;
    });

    window.mitekScienceSDK.cmd(MitekScienceCommands.SDK_STOP);
  }

  private checkCameraStatus(): void {
    this.cameraService.checkCameraPermission().subscribe(status => {
      this.cameraPermissionStatus = status;
    });
  }
}
